import { z } from "zod";
import { itemTypeSchema, scoringRuleSchema } from "./items";
import { examCodeSchema, programmeCodeSchema } from "./programmes";

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD");

/** A blueprint dimension, e.g. NCLEX Client Needs. */
export const blueprintDimensionSchema = z
  .object({
    key: z.string().min(1),
    name: z.string().min(1),
    /** "percent" weights sum to 100; "items" weights are scored-item counts. */
    weight_unit: z.enum(["percent", "items"]),
    categories: z
      .array(
        z.object({
          key: z.string().min(1),
          name: z.string().min(1),
          /** Midpoint used for mock assembly and readiness weighting. */
          weight: z.number().positive(),
          /** The official range, e.g. NCLEX "15–21%". Keys stay stable when an exam body renames a category. */
          range: z.object({ min: z.number().nonnegative(), max: z.number().positive() }).optional(),
          /** Grouping heading from the official outline, e.g. "Physiological Integrity". */
          parent: z.string().min(1).optional(),
        }),
      )
      .min(2),
  })
  .superRefine((d, ctx) => {
    const keys = d.categories.map((c) => c.key);
    if (new Set(keys).size !== keys.length) ctx.addIssue({ code: "custom", message: "Duplicate category key" });
    for (const c of d.categories) {
      if (c.range && (c.range.min > c.weight || c.weight > c.range.max)) {
        ctx.addIssue({ code: "custom", message: `${c.key}: weight ${c.weight} is outside its range ${c.range.min}–${c.range.max}` });
      }
    }
    if (d.weight_unit === "percent") {
      const sum = d.categories.reduce((s, c) => s + c.weight, 0);
      if (Math.abs(sum - 100) > 0.5) ctx.addIssue({ code: "custom", message: `Weights sum to ${sum}, expected 100` });
    }
  });
export type BlueprintDimension = z.infer<typeof blueprintDimensionSchema>;

export const mockSectionSchema = z.object({
  name: z.string().min(1),
  items: z.number().int().positive(),
  minutes: z.number().int().positive().nullable(),
  item_types: z.array(itemTypeSchema).min(1),
});

/** Format pack (§13.4a): item types, real-exam mock structure, scoring, delivery mode. */
export const formatPackSchema = z
  .object({
    version: z.string().min(1),
    item_types: z.array(itemTypeSchema).min(1),
    scoring: z.array(scoringRuleSchema).min(1),
    delivery: z.enum(["adaptive", "fixed"]),
    full_length: z.object({
      total_items: z.number().int().positive().nullable(),
      scored_items: z.number().int().positive().nullable(),
      pretest_items: z.number().int().nonnegative().nullable(),
      adaptive_range: z.object({ min: z.number().int().positive(), max: z.number().int().positive() }).nullable(),
      total_minutes: z.number().int().positive(),
      sections: z.array(mockSectionSchema).min(1),
      section_order_note: z.string().nullable(),
    }),
    cut_scores: z.array(z.object({ key: z.string(), name: z.string() })),
    unfolding_cases: z.object({ per_exam: z.number().int().nonnegative(), items_per_case: z.number().int().nonnegative() }).nullable(),
    pace_seconds_per_item: z.number().positive(),
    negative_stems_allowed: z.boolean(),
    notes: z.string(),
    /** Integrated processes from the official test plan (NCLEX: Caring, Clinical Judgment, …). */
    integrated_processes: z.array(z.string().min(1)).optional(),
    /** Clinical judgment model the exam measures (NCLEX: NCJMM, six steps; case studies + stand-alone items). */
    clinical_judgment: z
      .object({
        model: z.string().min(1),
        steps: z.array(z.object({ key: z.string().min(1), name: z.string().min(1) })).min(1),
        /** Share of the exam that is stand-alone clinical judgment items (bow-tie, trend), e.g. 0.10. */
        standalone_share: z.number().min(0).max(1),
      })
      .optional(),
    /** How a real adaptive exam ends (NCLEX: 95% confidence, maximum length, run out of time). */
    stopping_rules: z.array(z.enum(["confidence_95", "max_length", "run_out_of_time"])).optional(),
    /** Optional breaks offered during the real exam; the clock keeps running. */
    breaks: z.array(z.object({ after_minutes: z.number().int().positive() })).optional(),
    /** Official passing standard, for reference only — never used to predict a pass (scores aren't calibrated). */
    passing_standard: z.object({ value: z.number(), unit: z.literal("logits"), effective_to: isoDate.nullable() }).optional(),
  })
  .superRefine((p, ctx) => {
    for (const s of p.full_length.sections) {
      for (const t of s.item_types) {
        if (!p.item_types.includes(t)) ctx.addIssue({ code: "custom", message: `Section ${s.name} uses ${t}, not in pack` });
      }
    }
    if ((p.delivery === "adaptive") !== (p.full_length.adaptive_range !== null)) {
      ctx.addIssue({ code: "custom", message: "adaptive_range is required exactly when delivery is adaptive" });
    }
  });
export type FormatPack = z.infer<typeof formatPackSchema>;

export const examConfigSchema = z
  .object({
    programme: programmeCodeSchema,
    exam: examCodeSchema,
    exam_name: z.string().min(1),
    config_version: z.string().min(1),
    style_pack_version: z.string().min(1),
    effective_from: isoDate,
    effective_to: isoDate.nullable(),
    blueprint: z.array(blueprintDimensionSchema).min(1),
    format_pack: formatPackSchema,
    difficulty_mix: z.object({ easy: z.number(), medium: z.number(), hard: z.number() }),
    questions_per_set: z.number().int().positive(),
    cards_per_set: z.number().int().positive(),
    /** false until the weights are checked against the exam body's current official outline. */
    blueprint_verified: z.boolean(),
    source_note: z.string(),
  })
  .superRefine((c, ctx) => {
    const mix = c.difficulty_mix.easy + c.difficulty_mix.medium + c.difficulty_mix.hard;
    if (mix !== 100) ctx.addIssue({ code: "custom", message: `difficulty_mix sums to ${mix}` });
    if (c.effective_to && c.effective_to <= c.effective_from) ctx.addIssue({ code: "custom", message: "effective_to must be after effective_from" });
  });
export type ExamConfig = z.infer<typeof examConfigSchema>;

/**
 * The config a student preps with: the one in force on their exam date (spec §13.4a), falling back to
 * today's when no exam date is set. Returns null if nothing covers that date.
 */
export function configInForce<T extends Pick<ExamConfig, "exam" | "effective_from" | "effective_to">>(
  configs: T[],
  exam: ExamConfig["exam"],
  onDate: string,
): T | null {
  return (
    configs
      .filter((c) => c.exam === exam && c.effective_from <= onDate && (c.effective_to === null || onDate <= c.effective_to))
      .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0] ?? null
  );
}
